import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Activity, RefreshCw, AlertTriangle } from 'lucide-react';
import { projectId } from '../utils/supabase/info';

interface KVDiagnosticsProps {
  accessToken: string;
}

export function KVDiagnostics({ accessToken }: KVDiagnosticsProps) {
  const [loading, setLoading] = useState(false);
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [error, setError] = useState('');

  const runDiagnostics = async () => {
    setLoading(true);
    setError('');
    console.log('[KV Diagnostics] Starting diagnostic scan...');
    
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/kv-diagnostics`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDiagnostics(data);
        console.log('[KV Diagnostics] Results:', data);
      } else {
        throw new Error('Failed to run diagnostics');
      }
    } catch (err: any) {
      console.error('[KV Diagnostics] Error:', err);
      setError(err.message || 'Failed to run diagnostics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Database Performance Diagnostics
            </CardTitle>
            <CardDescription>
              Analyze KV store performance and identify potential bottlenecks
            </CardDescription>
          </div>
          <Button 
            onClick={runDiagnostics} 
            disabled={loading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Scanning...' : 'Run Diagnostics'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && (
          <div className="text-center py-8 text-muted-foreground">
            <p>Scanning database... This may take a few moments.</p>
            <p className="mt-2">Check the browser console for detailed progress.</p>
          </div>
        )}

        {diagnostics && !loading && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-muted-foreground">Total Scan Time</p>
                <p className="text-2xl">{diagnostics.totalTime}</p>
              </div>
            </div>

            {diagnostics.warning && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{diagnostics.warning}</AlertDescription>
              </Alert>
            )}

            {diagnostics.userBreakdown && (
              <div>
                <h3 className="mb-2">User Data Analysis</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-muted-foreground">Total Users</p>
                      <p className="text-2xl">{diagnostics.userBreakdown?.total?.toLocaleString() || '0'}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-muted-foreground">Company Users</p>
                      <p className="text-2xl">{diagnostics.userBreakdown?.companyUsers?.toLocaleString() || '0'}</p>
                      <p className="text-xs text-muted-foreground mt-1">With companyId</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-muted-foreground">Firm Users</p>
                      <p className="text-2xl">{diagnostics.userBreakdown?.firmUsers?.toLocaleString() || '0'}</p>
                      <p className="text-xs text-muted-foreground mt-1">Consultants/CF Admin</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-muted-foreground">Companies w/ Users</p>
                      <p className="text-2xl">{diagnostics.userBreakdown?.companiesWithUsers?.toLocaleString() || '0'}</p>
                      <p className="text-xs text-muted-foreground mt-1">Have company users</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {diagnostics.auditBreakdown && (
              <div>
                <h3 className="mb-2">Audit Log Breakdown</h3>
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-muted-foreground">Total Audit Logs</p>
                      <p className="text-2xl">{diagnostics.auditBreakdown.total.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-muted-foreground">Global Logs</p>
                      <p className="text-2xl">{diagnostics.auditBreakdown.global.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-muted-foreground">Company-Specific</p>
                      <p className="text-2xl">{diagnostics.auditBreakdown.companySpecific.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            <div>
              <h3 className="mb-2">Record Counts by Type</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Record Type</TableHead>
                    <TableHead>Count</TableHead>
                    <TableHead>Load Time</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(diagnostics.counts).map(([prefix, count]: [string, any]) => {
                    const timing = diagnostics.timings[prefix];
                    const isSlow = timing > 5000; // Over 5 seconds
                    const isVeryLarge = count > 1000;
                    
                    return (
                      <TableRow key={prefix}>
                        <TableCell className="font-mono">{prefix}</TableCell>
                        <TableCell>
                          {count.toLocaleString()}
                          {isVeryLarge && (
                            <Badge variant="destructive" className="ml-2">Very Large</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {timing}ms
                          {isSlow && (
                            <Badge variant="destructive" className="ml-2">Slow</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {isSlow || isVeryLarge ? (
                            <Badge variant="destructive">Needs Attention</Badge>
                          ) : (
                            <Badge variant="default">OK</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <h4 className="mb-2">Performance Recommendations</h4>
              <ul className="space-y-1 text-sm">
                <li>✓ Database caching is now enabled - tabs load instantly after first view</li>
                <li>✓ Backend queries have been optimized to reduce redundant data loading</li>
                {diagnostics.auditBreakdown?.total > 500 && (
                  <li className="text-destructive">Consider implementing audit log archiving for logs older than 90 days</li>
                )}
                {diagnostics.counts['company_data:'] > 50 && (
                  <li className="text-destructive">Large number of companies - consider pagination if experiencing slowdowns</li>
                )}
              </ul>
            </div>
          </div>
        )}

        {!diagnostics && !loading && (
          <div className="text-center py-8 text-muted-foreground">
            <p>Click "Run Diagnostics" to analyze database performance</p>
            <p className="mt-2">This will help identify performance bottlenecks</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}